// Proje Detay eylem paneli (Finar UX P2 Ö2) — ProjectDetail'den çıkarıldı (600 satır kuralı).
// Hiyerarşi: tek birincil ("Forma Devam Et") → onay sonrası çıkan sonuç eylemleri (Final
// rapor, Tamamla) → ikincil grup (AI üretimi, gözden geçirme, rapordan doldur) → riskli/seyrek
// eylemler "Diğer ⋯" menüsünde (Tümünü Yeniden Üret, Arşive Al). Arşive Al artık
// `window.confirm` değil `ConfirmDialog`; Yeniden Üret kendi onay penceresini (üst bileşen)
// açmaya devam eder.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Unlock } from "lucide-react";
import type { DocumentExportResponse, Project } from "@mahir/shared";
import { api, ApiError } from "../../api/client";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { MoreMenu, type MoreMenuItem } from "../common/MoreMenu";
import { PROJECT_ACTIONS_COPY as C } from "../../lib/project-actions-copy";
import { IMPORT_COPY } from "../../lib/report-import-ui";

export interface ProjectActionsProps {
  projectId: string;
  project: Project;
  onProjectChange: (p: Project) => void;
  reviewSummary: { total: number; approved: number } | null;
  currentVersion: number;
  canReopen: boolean;
  canImportReport: boolean;
  importActive: boolean;
  onOpenGenerate: () => void;
  onOpenRegenerate: () => void;
  onOpenFill: () => void;
  /** Final rapor indirildikten sonra belge listesi yenilensin (P3-30). */
  onDocumentsChanged: () => void;
}

const secondaryStyle = {
  background: "transparent",
  border: "1px solid var(--color-border)",
  color: "var(--color-text-2)",
} as const;

const errorLine = (msg: string | null): JSX.Element | null =>
  msg ? (
    <div className="text-xs px-2" role="alert" style={{ color: "var(--color-error)" }}>
      {msg}
    </div>
  ) : null;

export function ProjectActions(props: ProjectActionsProps): JSX.Element {
  const { projectId: id, project, onProjectChange, reviewSummary, currentVersion } = props;
  const [completeLoading, setCompleteLoading] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [finalLoading, setFinalLoading] = useState(false);
  const [finalError, setFinalError] = useState<string | null>(null);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const allApproved =
    reviewSummary !== null &&
    reviewSummary.total > 0 &&
    reviewSummary.approved === reviewSummary.total;

  function markCompleted(failMsg: string, after?: () => void): void {
    setCompleteLoading(true);
    setCompleteError(null);
    api<{ project: Project }>(`/projects/${id}`, { method: "PATCH", body: { status: "completed" } })
      .then((r) => onProjectChange(r.project))
      .catch((err: unknown) => setCompleteError(err instanceof ApiError ? err.message : failMsg))
      .finally(() => {
        setCompleteLoading(false);
        after?.();
      });
  }

  function downloadFinal(): void {
    setFinalLoading(true);
    setFinalError(null);
    api<DocumentExportResponse>(`/projects/${id}/documents/export/full`)
      .then((res) => {
        window.open(res.downloadUrl, "_blank");
        props.onDocumentsChanged();
      })
      .catch((err: unknown) =>
        setFinalError(err instanceof ApiError ? err.message : C.finalDownloadFailed),
      )
      .finally(() => setFinalLoading(false));
  }

  function reopen(): void {
    const reason = window.prompt(C.reopenPrompt, "");
    if (reason === null) return;
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setReopenError(C.reopenTooShort);
      return;
    }
    setReopenLoading(true);
    setReopenError(null);
    api<{ project: Project }>(`/projects/${id}/reopen`, {
      method: "POST",
      body: { reason: trimmed },
    })
      .then((res) => onProjectChange(res.project))
      .catch((err: unknown) =>
        setReopenError(err instanceof ApiError ? err.message : C.reopenFailed),
      )
      .finally(() => setReopenLoading(false));
  }

  const moreItems: MoreMenuItem[] = [];
  if (currentVersion > 0) {
    moreItems.push({
      key: "regenerate",
      label: C.regenerateAll(currentVersion + 1),
      title: C.regenerateAllTitle(currentVersion),
      onSelect: props.onOpenRegenerate,
    });
  }
  if (project.status !== "completed") {
    moreItems.push({
      key: "archive",
      label: C.archive,
      title: C.archiveTitle,
      tone: "danger",
      disabled: completeLoading,
      onSelect: () => setArchiveOpen(true),
    });
  }

  return (
    <div className="flex flex-col gap-2" style={{ minWidth: 220 }}>
      {/* Birincil eylem — tek dolu düğme. */}
      <Link
        to={`/projects/${id}/form`}
        className="px-4 py-2.5 rounded-lg font-semibold text-sm text-center"
        style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
      >
        {C.continueForm}
      </Link>

      {/* Sonuç eylemleri — yalnız tüm bölümler onaylıyken (#018/#019). */}
      {allApproved && (
        <>
          <button
            onClick={downloadFinal}
            disabled={finalLoading}
            className="px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{
              background: "var(--color-success)",
              color: "var(--color-surface)",
              border: "none",
            }}
            title={C.finalDownloadTitle}
          >
            <Download size={16} aria-hidden="true" />
            {finalLoading ? C.preparing : C.finalDownload}
          </button>
          {errorLine(finalError)}
          {(project.status === "in_progress" || project.status === "draft") && (
            <button
              onClick={() => markCompleted(C.completeFailed)}
              disabled={completeLoading}
              className="px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
              style={{
                background: "transparent",
                border: "1px solid var(--color-success)",
                color: "var(--color-success)",
                fontWeight: 600,
              }}
              title={C.completeTitle}
            >
              {completeLoading ? C.busy : C.complete}
            </button>
          )}
        </>
      )}

      {/* İkincil grup. */}
      <div className="flex flex-col gap-2" role="group" aria-label={C.secondaryGroup}>
        <button
          onClick={props.onOpenGenerate}
          className="px-4 py-2.5 rounded-lg text-sm"
          style={{
            background: "transparent",
            border: "1px solid var(--color-primary)",
            color: "var(--color-primary)",
            fontWeight: 600,
          }}
        >
          {C.startGeneration}
        </button>
        {currentVersion > 0 && (
          <Link
            to={`/projects/${id}/review`}
            className="px-4 py-2 rounded-lg text-xs text-center"
            style={{ ...secondaryStyle, fontWeight: 600 }}
          >
            {C.review}
          </Link>
        )}
        {/* Task 14 (3A) — aktif import sürerken disabled + title. */}
        {props.canImportReport && project.status !== "completed" && (
          <button
            onClick={props.onOpenFill}
            disabled={props.importActive}
            title={props.importActive ? C.importActive : undefined}
            className="px-4 py-2 rounded-lg text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            style={secondaryStyle}
          >
            {IMPORT_COPY.fillFromReport}
          </button>
        )}
        <MoreMenu label={C.more} ariaLabel={C.moreAria} items={moreItems} />
        {errorLine(completeError)}
      </div>

      {props.canReopen && project.status === "completed" && (
        <button
          onClick={reopen}
          disabled={reopenLoading}
          className="px-4 py-2 rounded-lg text-xs disabled:opacity-50 inline-flex items-center justify-center gap-2"
          style={{
            background: "transparent",
            border: "1px solid var(--color-warning-text)",
            color: "var(--color-warning-text)",
            fontWeight: 600,
          }}
          title={C.reopenTitle}
        >
          <Unlock size={14} aria-hidden="true" />
          {reopenLoading ? C.reopening : C.reopen}
        </button>
      )}
      {errorLine(reopenError)}

      <ConfirmDialog
        open={archiveOpen}
        title={C.archiveConfirmTitle}
        body={C.archiveConfirmBody}
        confirmLabel={C.archiveConfirmAction}
        tone="danger"
        busy={completeLoading}
        onCancel={() => setArchiveOpen(false)}
        onConfirm={() => markCompleted(C.archiveFailed, () => setArchiveOpen(false))}
      />
    </div>
  );
}
